import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getDevices, getAlertEvents, getAlertJobs } from '../api/alertApi';
import StatusBadge from '../components/StatusBadge';

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 24px', minWidth: 140 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default function AlertDashboard() {
  const { data: devicesData }  = useQuery({ queryKey: ['devices'], queryFn: getDevices });
  const { data: eventsData }   = useQuery({ queryKey: ['events-all'], queryFn: () => getAlertEvents(null, { limit: 10 }) });
  const { data: jobsPending }  = useQuery({ queryKey: ['jobs-pending'], queryFn: () => getAlertJobs(null, { status: 'pending', limit: 1 }) });
  const { data: jobsFailed }   = useQuery({ queryKey: ['jobs-failed'],  queryFn: () => getAlertJobs(null, { status: 'failed',  limit: 1 }) });

  const devices  = devicesData?.data  || [];
  const events   = eventsData?.data   || [];
  const pending  = jobsPending?.meta?.total  || 0;
  const failed   = jobsFailed?.meta?.total   || 0;
  const critical = events.filter(e => e.severity === 'critical').length;

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Alert Dashboard</h1>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        <StatCard label="Active Devices"    value={devices.length} color="#2563eb" />
        <StatCard label="Recent Events"     value={eventsData?.meta?.total || 0} color="#d97706" />
        <StatCard label="Critical Alerts"   value={critical}       color="#dc2626" />
        <StatCard label="Jobs Pending"      value={pending}        color="#6366f1" />
        <StatCard label="Jobs Failed"       value={failed}         color="#dc2626" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Devices */}
        <div style={card}>
          <div style={cardHead}>
            <span>Devices</span>
            <Link to="/settings" style={linkBtn}>Manage</Link>
          </div>
          <table style={tbl}>
            <thead><tr><th style={th}>Device ID</th><th style={th}>Name</th><th style={th}>Alerts</th></tr></thead>
            <tbody>
              {devices.map(d => (
                <tr key={d.id}>
                  <td style={td}><code>{d.device_id}</code></td>
                  <td style={td}>{d.name}</td>
                  <td style={td}>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10,
                      background: d.alertSetting?.alerts_enabled ? '#dcfce7' : '#f3f4f6',
                      color: d.alertSetting?.alerts_enabled ? '#166534' : '#6b7280' }}>
                      {d.alertSetting?.alerts_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recent Events */}
        <div style={card}>
          <div style={cardHead}>
            <span>Recent Alert Events</span>
            <Link to="/history" style={linkBtn}>View All</Link>
          </div>
          <table style={tbl}>
            <thead><tr><th style={th}>Device</th><th style={th}>Parameter</th><th style={th}>Severity</th><th style={th}>Time</th></tr></thead>
            <tbody>
              {events.map(e => (
                <tr key={e.id}>
                  <td style={td}><code>{e.device_id}</code></td>
                  <td style={td}>{e.parameter}</td>
                  <td style={td}><StatusBadge value={e.severity} /></td>
                  <td style={td} title={e.triggered_at}>{new Date(e.triggered_at).toLocaleString()}</td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={4} style={{ ...td, color: '#9ca3af', textAlign: 'center' }}>No events</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const card     = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 };
const cardHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontWeight: 700, fontSize: 16 };
const linkBtn  = { fontSize: 13, color: '#2563eb', textDecoration: 'none' };
const tbl  = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th   = { padding: '7px 10px', background: '#f9fafb', textAlign: 'left', fontWeight: 600 };
const td   = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6' };
